import React from "react";

function useInput<T extends HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
  defaultValue?: string,
  useState = React.useState,
) {
  const isFirstRun = React.useRef(true);
  const pristine = React.useRef(true);
  const [value, setValueState] = useState(defaultValue || "");

  const setValue = React.useCallback((value: React.SetStateAction<string>) => {
    setValueState(value);
    pristine.current = false;
  }, []);

  const onChange = React.useCallback(
    (e: React.FormEvent<T>) => {
      setValue(e.currentTarget.value);
    },
    [setValue],
  );

  React.useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    // defaultValue changed, resetting form
    if (defaultValue !== undefined) {
      setValueState(defaultValue);
      pristine.current = true;
    }
  }, [defaultValue, setValue]);

  return React.useMemo(
    () => ({
      value,
      defaultValue,
      setValue,
      pristine: pristine.current,
      dirty: value !== (defaultValue || ""),
      onChange,
    }),
    [defaultValue, value, setValue, onChange],
  );
}

export default useInput;

export type Input = ReturnType<typeof useInput>;
